<template>
  <div class="global-player-control" data-recorder-control="true">
    <h4>回放控制</h4>
    <input
      type="file"
      accept=".json"
      @change="handleFileUpload"
      class="file-input"
      data-recorder-control="true"
    />
    <button
      @click="startPlayback"
      :disabled="!recordingData || isPlaying"
      class="play-btn"
      data-recorder-control="true"
    >
      {{ isPlaying ? "正在回放..." : "开始回放" }}
    </button>
    <!-- 显示自然语言步骤 -->
    <div
      v-if="recordingData?.naturalLanguage"
      class="script-preview"
      data-recorder-control="true"
    >
      <h5>执行步骤预览：</h5>
      <pre>{{ recordingData.naturalLanguage }}</pre>
    </div>
    <p class="play-status" v-if="playStatus" data-recorder-control="true">
      {{ playStatus }}
    </p>
  </div>
</template>

<script>
export default {
  name: "GlobalPlayerControl",
  data() {
    return {
      recordingData: null,
      isPlaying: false,
      playStatus: "",
    };
  },
  methods: {
    handleFileUpload(e) {
      const file = e.target.files[0];
      if (!file) {
        this.playStatus = "请选择有效的 JSON 录制文件";
        return;
      }
      if (file.name.split(".").pop().toLowerCase() !== "json") {
        this.playStatus = "请上传 JSON 格式的录制文件";
        e.target.value = "";
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const parsedData = JSON.parse(event.target.result);

          if (typeof parsedData !== "object" || parsedData === null) {
            throw new Error("录制文件格式错误，不是有效的 JSON 对象");
          }

          if (!parsedData.actions || !Array.isArray(parsedData.actions)) {
            throw new Error("无效的录制文件，actions 必须是数组");
          }

          this.recordingData = parsedData;
          this.playStatus = parsedData.actions.length
            ? `文件解析成功！共 ${parsedData.actions.length} 个操作`
            : "文件解析成功，但未包含任何操作";

          // 若有自然语言步骤，显示提示
          if (parsedData.naturalLanguage) {
            this.playStatus += "（包含自然语言步骤）";
          }

          e.target.value = "";
        } catch (err) {
          this.playStatus = `文件解析失败：${err.message}`;
          this.recordingData = null;
          e.target.value = "";
        }
      };
      reader.onerror = () => {
        this.playStatus = "文件读取失败，请检查文件是否损坏";
        this.recordingData = null;
      };
      reader.readAsText(file);
    },

    async startPlayback() {
      if (!this.$router) {
        this.playStatus = "路由实例未初始化，无法回放";
        return;
      }
      if (!this.recordingData) {
        this.playStatus = "请先上传有效的录制文件";
        return;
      }

      this.isPlaying = true;
      this.playStatus = "开始回放...";
      const actions = this.recordingData.actions;

      try {
        // 跳转到初始页面（使用完整URL解析）
        const initialUrl =
          this.recordingData.initialUrl || this.recordingData.url;
        if (initialUrl) {
          const targetPath = this.getPathFromUrl(initialUrl);
          if (targetPath) {
            await this.$router.push(targetPath);
            await this.waitForPageLoad();
          } else {
            throw new Error(`无效的初始地址：${initialUrl}`);
          }
        }

        // 执行所有操作
        for (let i = 0; i < actions.length; i++) {
          const action = actions[i];
          
          // 跳过未知元素
          if (action.selector === 'unknown') {
            console.log('Skipping unknown element');
            continue;
          }
          
          // 优先使用自然语言描述作为状态提示
          const stepDesc = this.getStepDescription(action, i + 1);
          this.playStatus = `回放中：${stepDesc}`;

          try {
            switch (action.type) {
            case "dialog-close":
              // 关闭对话框操作 - 尝试多种方式
              console.log('Attempting to close dialog...');
              
              // 方法1: 查找并点击关闭按钮（最常见）
              let closeBtn = document.querySelector('.el-dialog__headerbtn');
              if (closeBtn) {
                console.log('Found close button, clicking...');
                closeBtn.click();
                await this.wait(300);
                
                // 验证是否关闭
                if (!document.querySelector('.el-dialog__wrapper')) {
                  console.log('Dialog closed successfully');
                  break;
                }
              }
              
              // 方法2: 查找关闭图标内的 i 标签
              const closeIcon = document.querySelector('.el-dialog__headerbtn i');
              if (closeIcon) {
                console.log('Found close icon i, clicking...');
                closeIcon.click();
                await this.wait(300);
                
                if (!document.querySelector('.el-dialog__wrapper')) {
                  console.log('Dialog closed successfully via icon');
                  break;
                }
              }
              
              // 方法3: 查找所有可见的关闭按钮
              const allCloseBtns = document.querySelectorAll('.el-dialog__headerbtn');
              for (let btn of allCloseBtns) {
                const rect = btn.getBoundingClientRect();
                if (rect.width > 0 && rect.height > 0) {
                  console.log('Found visible close button, clicking...');
                  btn.click();
                  await this.wait(300);
                  
                  if (!document.querySelector('.el-dialog__wrapper')) {
                    console.log('Dialog closed successfully via visible button');
                    break;
                  }
                }
              }
              
              // 方法4: 触发 ESC 键关闭
              console.log('Attempting ESC key close...');
              const escEvent = new KeyboardEvent('keydown', {
                key: 'Escape',
                code: 'Escape',
                keyCode: 27,
                which: 27,
                bubbles: true,
                cancelable: true
              });
              document.dispatchEvent(escEvent);
              await this.wait(300);
              
              if (!document.querySelector('.el-dialog__wrapper')) {
                console.log('Dialog closed successfully via ESC');
                break;
              }
              
              // 方法5: 点击遮罩层（如果允许）
              const dialogWrapper = document.querySelector('.el-dialog__wrapper');
              if (dialogWrapper) {
                console.log('Attempting wrapper click...');
                dialogWrapper.click();
                await this.wait(300);
              }
              
              console.log('Dialog close attempt completed');
              break;
            case "click":
              const clickElement = await this.waitForElement(
                action.selector,
                action
              );
              clickElement.click();
              await this.wait(800);
              break;
            case "input":
              const inputElement = await this.waitForElement(
                action.selector,
                action
              );
              inputElement.value = action.value;
              inputElement.dispatchEvent(new Event("input", { bubbles: true }));
              // 触发change事件确保表单验证生效
              inputElement.dispatchEvent(
                new Event("change", { bubbles: true })
              );
              await this.wait(600);
              break;
            case "quill-input":
              const quillEditor = await this.waitForElement(
                action.selector,
                action
              );
              if (quillEditor) {
                // 设置 Quill 编辑器的内容
                quillEditor.innerHTML = action.value;
                // 触发 Vue 的更新
                const event = new Event("input", { bubbles: true });
                quillEditor.dispatchEvent(event);
              }
              await this.wait(600);
              break;
            case "select":
              const selectElement = await this.waitForElement(
                action.selector,
                action
              );

              // 检查是否是 Element UI 的 select
              if (
                selectElement.classList &&
                selectElement.classList.contains("el-input__inner")
              ) {
                // Element UI select 的回放
                selectElement.click(); // 打开下拉框
                await this.wait(500); // 等待下拉框完全展开

                // 查找匹配的选项
                const options = document.querySelectorAll(
                  ".el-select-dropdown__item, .el-option"
                );
                let optionFound = false;

                for (let option of options) {
                  if (
                    option.textContent.trim() === action.selectedText ||
                    option.getAttribute("value") === action.value
                  ) {
                    option.click();
                    optionFound = true;
                    break;
                  }
                }

                // 如果没找到普通选项，可能是 el-tree 在 el-select 中
                if (!optionFound) {
                  // 查找 tree 节点
                  const treeNodes = document.querySelectorAll(
                    ".el-tree-node__content"
                  );
                  for (let node of treeNodes) {
                    const nodeText = node.querySelector(".el-tree-node__label");
                    if (
                      nodeText &&
                      nodeText.textContent.trim() === action.selectedText
                    ) {
                      node.click();
                      optionFound = true;
                      break;
                    }
                  }
                }

                if (!optionFound) {
                  console.warn(`未找到匹配的选项: ${action.selectedText}`);
                }
              } else {
                // 原生 select 的回放
                selectElement.value = action.value;
                selectElement.dispatchEvent(
                  new Event("change", { bubbles: true })
                );
              }
              await this.wait(600);
              break;
            case "radio":
            case "checkbox":
              console.log('Processing checkbox/radio action:', {
                type: action.type,
                selector: action.selector,
                checked: action.checked,
                rawSelector: JSON.stringify(action.selector)
              });
              
              // 修复选择器格式
              let fixedCheckboxSelector = action.selector;
              
              // 如果选择器包含损坏的 class 属性，转换为 type 选择器
              if (fixedCheckboxSelector.includes('class~=') || fixedCheckboxSelector.includes('el-checkbox')) {
                const dataAttrs = fixedCheckboxSelector.match(/\[data-[^\]]+\]/g);
                if (dataAttrs) {
                  fixedCheckboxSelector = 'input[type="checkbox"]' + dataAttrs.join('');
                  console.log('Converted checkbox selector to type format:', fixedCheckboxSelector);
                }
              }
              
              const checkElement = await this.waitForElement(
                fixedCheckboxSelector,
                action
              );
              console.log('Found checkbox/radio element:', checkElement);
              
              // 检查当前状态
              if (checkElement.checked !== action.checked) {
                console.log('Setting checkbox state from', checkElement.checked, 'to', action.checked);
                
                // 直接设置 checked 属性，避免触发业务逻辑
                checkElement.checked = action.checked;
                
                // 手动触发必要的事件，但不触发 click 事件（避免业务逻辑）
                // 只触发 change 和 input 事件来更新 Vue 的数据绑定
                const changeEvent = new Event('change', { bubbles: true });
                checkElement.dispatchEvent(changeEvent);
                
                const inputEvent = new Event('input', { bubbles: true });
                checkElement.dispatchEvent(inputEvent);
                
                console.log('Checkbox state updated without triggering click');
              } else {
                console.log('Checkbox already in correct state:', action.checked);
              }
              
              await this.wait(600);
              break;
            case "page_switch":
              const toPath = this.getPathFromUrl(action.toUrl);
              if (toPath) {
                await this.$router.push(toPath);
                await this.waitForPageLoad();
              } else {
                throw new Error(`无效的目标地址：${action.toUrl}`);
              }
              break;
            default:
              await this.wait(300);
              break;
          }
          } catch (err) {
            // 如果是 v-show 隐藏的元素，跳过并继续
            if (err.message && err.message.includes('v-show 隐藏')) {
              console.warn(`跳过被 v-show 隐藏的元素: ${action.selector}`);
              this.playStatus = `跳过：${stepDesc}（元素被隐藏）`;
              await this.wait(500);
              continue;
            }
            // 其他错误，抛出
            throw err;
          }
        }

        this.playStatus = "✅ 所有操作回放完成！";
        this.isPlaying = false;
      } catch (err) {
        this.playStatus = `❌ 回放失败：${err.message}`;
        this.isPlaying = false;
        console.error("回放错误：", err);
      }
    },

    // 获取步骤的自然语言描述
    getStepDescription(action, index) {
      // 如果有完整的自然语言脚本，尝试提取对应步骤
      if (this.recordingData.naturalLanguage) {
        const lines = this.recordingData.naturalLanguage.split("\n");
        const stepLine = lines.find((line) => line.startsWith(`${index}.`));
        if (stepLine) return stepLine;
      }
      //  fallback：使用默认描述
      const descriptions = {
        "dialog-close": "关闭对话框",
        click: `点击元素（${action.selector}）`,
        input: `输入内容“${action.value}”`,
        page_switch: `切换到页面 ${action.toUrl}`,
        recording_start: "准备开始",
      };
      return descriptions[action.type] || `执行操作 ${action.type}`;
    },

    // 工具方法：从URL提取路径（支持 Vue Router hash 模式）
    getPathFromUrl(url) {
      try {
        const parsedUrl = new URL(url);

        // 如果有 hash，说明是 Vue Router 的 hash 模式
        if (parsedUrl.hash) {
          // 提取 hash 中的路径部分（去掉开头的 #）
          return parsedUrl.hash.substring(1);
        }

        // 否则返回普通路径
        return parsedUrl.pathname + parsedUrl.search;
      } catch (e) {
        // 解析失败，可能已经是路径格式
        if (url.startsWith("/")) {
          return url;
        }
        return url;
      }
    },

    // 增强的元素查找逻辑（支持文本匹配和特殊选择器）
    findElement(selector, action = null) {
      console.log('findElement called with:', { selector, action });
      
      // 跳过未知元素
      if (selector === 'unknown') {
        console.log('Skipping unknown selector');
        return null;
      }
      
      // 特殊处理：v-for 选择器（包含 data-vfor-index 属性）
      // 格式：.one-project[data-vfor-index="0"] img.pic
      if (selector.includes('[data-vfor-index="')) {
        console.log('Detected v-for selector with data-vfor-index');
        
        const match = selector.match(/\.([a-z-]+)\[data-vfor-index="(\d+)"\]\s+(.+)/);
        if (match) {
          const containerClass = match[1];  // 例如：one-project
          const vForIndex = parseInt(match[2]);  // 例如：0
          const elementPath = match[3];  // 例如：img.pic
          
          console.log('Parsed v-for selector:', { containerClass, vForIndex, elementPath });
          
          // 查找所有匹配的容器
          const containers = document.querySelectorAll('.' + containerClass);
          console.log('Found containers:', containers.length);
          
          if (containers[vForIndex]) {
            console.log('Found target container at index:', vForIndex);
            // 在目标容器中查找元素
            const element = containers[vForIndex].querySelector(elementPath);
            if (element) {
              console.log('Found element in container');
              return element;
            } else {
              console.log('Element not found in container, trying without state classes');
              // 尝试去掉动态状态类
              if (elementPath.includes('.')) {
                const parts = elementPath.split('.');
                const tag = parts[0];
                const classes = parts.slice(1).filter(cls => 
                  cls && 
                  !cls.includes('rotated') && !cls.includes('selected') &&
                  !cls.includes('open') && !cls.includes('closed') &&
                  !cls.includes('expanded') && !cls.includes('collapsed') &&
                  !cls.includes('active') && !cls.includes('hover')
                );
                
                if (classes.length > 0) {
                  const simplifiedPath = tag + '.' + classes.join('.');
                  const element = containers[vForIndex].querySelector(simplifiedPath);
                  if (element) {
                    console.log('Found element with simplified path:', simplifiedPath);
                    return element;
                  }
                } else if (tag) {
                  const element = containers[vForIndex].querySelector(tag);
                  if (element) {
                    console.log('Found element with tag only:', tag);
                    return element;
                  }
                }
              }
            }
          } else {
            console.log('Container index out of range');
          }
        }
        
        console.log('Could not find element with v-for selector');
        return null;
      }
      
      // 修复可能的选择器格式问题（双下划线被替换成空格或单下划线）
      selector = selector
        .replace(/@@/g, '__')  // 转换临时分隔符
        .replace(/el-checkbox\s+original/g, 'el-checkbox__original')
        .replace(/el-checkbox_original/g, 'el-checkbox__original')
        .replace(/input\.el-checkbox\s+original/g, 'input.el-checkbox__original')
        .replace(/input\.el-checkbox_original/g, 'input.el-checkbox__original')
        .replace(/el-input\s+inner/g, 'el-input__inner')
        .replace(/el-dialog\s+/g, 'el-dialog__');
      
      // 特殊处理：v-for 中的元素（如 .one-project:nth-of-type(1) img.pic）
      // 这种选择器可能因为 v-show 或数据加载问题找不到
      if (selector.includes(':nth-of-type(') && selector.includes(' ')) {
        console.log('Detected v-for selector with nth-of-type');
        
        // 尝试直接查找
        let element = document.querySelector(selector);
        if (element) {
          console.log('Found element directly');
          return element;
        }
        
        // 如果找不到，尝试分解选择器
        // 例如：.one-project:nth-of-type(1) img.pic
        const parts = selector.split(' ');
        if (parts.length >= 2) {
          const containerSelector = parts[0]; // .one-project:nth-of-type(1)
          let elementSelector = parts.slice(1).join(' '); // img.pic 或 img.pic.rotated
          
          console.log('Trying to find container:', containerSelector);
          const container = document.querySelector(containerSelector);
          
          if (container) {
            console.log('Found container, searching for element inside:', elementSelector);
            element = container.querySelector(elementSelector);
            if (element) {
              console.log('Found element inside container');
              return element;
            }
            
            // 如果找不到，尝试去掉动态状态类再查找（兼容旧录制）
            if (elementSelector.includes('.')) {
              const elementParts = elementSelector.split('.');
              const tag = elementParts[0]; // img
              const classes = elementParts.slice(1).filter(cls => 
                cls && 
                !cls.includes('rotated') && !cls.includes('selected') &&
                !cls.includes('open') && !cls.includes('closed') &&
                !cls.includes('expanded') && !cls.includes('collapsed') &&
                !cls.includes('active') && !cls.includes('hover')
              );
              
              if (classes.length > 0) {
                const simplifiedSelector = tag + '.' + classes.join('.');
                console.log('Trying simplified selector without state classes:', simplifiedSelector);
                element = container.querySelector(simplifiedSelector);
                if (element) {
                  console.log('Found element with simplified selector');
                  return element;
                }
              } else if (tag) {
                // 只用标签名查找
                console.log('Trying tag-only selector:', tag);
                element = container.querySelector(tag);
                if (element) {
                  console.log('Found element with tag-only selector');
                  return element;
                }
              }
            }
          }
          
          // 如果还是找不到，尝试查找所有匹配的容器
          const containerClass = containerSelector.split(':')[0]; // .one-project
          const nthMatch = containerSelector.match(/:nth-of-type\((\d+)\)/);
          
          if (containerClass && nthMatch) {
            const targetIndex = parseInt(nthMatch[1]) - 1; // 转换为0-based索引
            console.log('Searching all containers:', containerClass, 'target index:', targetIndex);
            
            const allContainers = document.querySelectorAll(containerClass);
            console.log('Found containers:', allContainers.length);
            
            if (allContainers[targetIndex]) {
              console.log('Found target container by index');
              element = allContainers[targetIndex].querySelector(elementSelector);
              if (element) {
                console.log('Found element in target container');
                return element;
              }
            }
          }
        }
        
        console.log('Could not find element with v-for selector');
      }
      
      // 特殊处理：Element UI input 的自定义选择器
      if (selector.includes("data-label=") || selector.includes("data-prop=") || selector.includes("placeholder=")) {
        console.log('Detected custom el-input selector');
        const labelMatch = selector.match(/data-label="([^"]+)"/);
        const propMatch = selector.match(/data-prop="([^"]+)"/);
        const placeholderMatch = selector.match(/placeholder="([^"]+)"/);

        const allInputs = document.querySelectorAll("input.el-input__inner");
        console.log('Found el-input elements:', allInputs.length);

        // 通过 label 查找
        if (labelMatch) {
          const targetLabel = labelMatch[1];
          console.log('Searching by label:', targetLabel);
          for (let input of allInputs) {
            const formItem = input.closest(".el-form-item");
            if (formItem) {
              const label = formItem.querySelector("label");
              if (label) {
                const labelText = label.textContent.trim();
                const sanitizedLabel = labelText.replace(/[^a-zA-Z0-9\u4e00-\u9fa5]/g, "-");
                console.log('Comparing labels:', { sanitizedLabel, targetLabel });
                if (sanitizedLabel === targetLabel) {
                  console.log('Found matching input by label');
                  return input;
                }
              }
            }
          }
        }

        // 通过 placeholder 查找
        if (placeholderMatch) {
          const targetPlaceholder = placeholderMatch[1];
          console.log('Searching by placeholder:', targetPlaceholder);
          for (let input of allInputs) {
            if (input.placeholder === targetPlaceholder) {
              console.log('Found matching input by placeholder');
              return input;
            }
          }
        }

        // 通过 prop 查找
        if (propMatch) {
          const targetProp = propMatch[1];
          console.log('Searching by prop:', targetProp);
          for (let input of allInputs) {
            const formItem = input.closest(".el-form-item");
            if (formItem && formItem.getAttribute("prop") === targetProp) {
              console.log('Found matching input by prop');
              return input;
            }
          }
        }

        // 回退到第一个匹配的 input
        console.log('Falling back to first el-input');
        return allInputs[0] || null;
      }

      // 特殊处理:Element UI checkbox 的自定义选择器
      if (selector.includes("data-checkbox") || 
          selector.includes("data-context") ||
          selector.includes('type="checkbox"')) {
        console.log('Detected checkbox selector:', selector);
        
        // 提取上下文和索引信息
        const contextMatch = selector.match(/data-context="([^"]+)"/);
        const tableIdMatch = selector.match(/data-table-id="([^"]+)"/);
        const tbodyIndexMatch = selector.match(/data-tbody-index="(\d+)"/);
        const globalMatch = selector.match(/data-global="(\d+)"/);
        const oldGlobalMatch = selector.match(/data-checkbox-global="(\d+)"/); // 兼容旧格式
        
        // 如果有 table ID，先找到对应的 table
        let targetTable = null;
        if (tableIdMatch) {
          const tableId = tableIdMatch[1];
          console.log('Searching for table with ID:', tableId);
          
          // 通过 super-tree-item 的 id 找到对应的 table
          const superTreeItem = document.getElementById(tableId);
          if (superTreeItem) {
            targetTable = superTreeItem.querySelector('table');
            console.log('Found target table:', !!targetTable);
          } else {
            console.log('Super tree item not found for ID:', tableId);
          }
        }
        
        // 优先使用 table ID + tbody 索引（最可靠）
        if (contextMatch && contextMatch[1] === 'tbody' && tbodyIndexMatch && targetTable) {
          const tbodyIndex = parseInt(tbodyIndexMatch[1]);
          console.log('Searching checkbox by tbody index in specific table:', tbodyIndex);
          
          // 在特定 table 的 tbody 中查找 checkbox
          const tbodyCheckboxes = targetTable.querySelectorAll('tbody input[type="checkbox"]');
          console.log('Found tbody checkbox elements in target table:', tbodyCheckboxes.length);
          
          if (tbodyCheckboxes[tbodyIndex]) {
            console.log('Found checkbox by tbody index in target table');
            return tbodyCheckboxes[tbodyIndex];
          } else {
            console.log('Tbody index out of range in target table');
          }
        }
        
        // 如果是 thead 中的checkbox（全选）且有 table ID
        if (contextMatch && contextMatch[1] === 'thead' && targetTable) {
          console.log('Searching checkbox in thead of specific table');
          const theadCheckboxes = targetTable.querySelectorAll('thead input[type="checkbox"]');
          console.log('Found thead checkbox elements in target table:', theadCheckboxes.length);
          
          if (theadCheckboxes.length > 0) {
            console.log('Found checkbox in thead of target table');
            return theadCheckboxes[0]; // 通常thead只有一个checkbox
          }
        }
        
        // 回退：使用全局 tbody 索引（兼容旧录制）
        if (contextMatch && contextMatch[1] === 'tbody' && tbodyIndexMatch) {
          const tbodyIndex = parseInt(tbodyIndexMatch[1]);
          console.log('Fallback: Searching checkbox by global tbody index:', tbodyIndex);
          
          // 查找所有 tbody 中的 checkbox
          const tbodyCheckboxes = document.querySelectorAll('tbody input[type="checkbox"]');
          console.log('Found tbody checkbox elements globally:', tbodyCheckboxes.length);
          
          if (tbodyCheckboxes[tbodyIndex]) {
            console.log('Found checkbox by global tbody index');
            return tbodyCheckboxes[tbodyIndex];
          } else {
            console.log('Global tbody index out of range');
          }
        }
        
        // 如果是 thead 中的checkbox（全选）
        if (contextMatch && contextMatch[1] === 'thead') {
          console.log('Fallback: Searching checkbox in thead globally');
          const theadCheckboxes = document.querySelectorAll('thead input[type="checkbox"]');
          console.log('Found thead checkbox elements globally:', theadCheckboxes.length);
          
          if (theadCheckboxes.length > 0) {
            console.log('Found checkbox in thead globally');
            return theadCheckboxes[0]; // 通常thead只有一个checkbox
          }
        }
        
        // 回退到全局索引
        const globalIndex = globalMatch ? parseInt(globalMatch[1]) : 
                           (oldGlobalMatch ? parseInt(oldGlobalMatch[1]) : null);
        
        if (globalIndex !== null) {
          console.log('Falling back to global index:', globalIndex);
          const allCheckboxes = document.querySelectorAll('input[type="checkbox"]');
          console.log('Found checkbox elements:', allCheckboxes.length);
          
          if (allCheckboxes[globalIndex]) {
            console.log('Found checkbox by global index');
            return allCheckboxes[globalIndex];
          } else {
            console.log('Global index out of range');
          }
        }
        
        // 最后回退：尝试直接使用选择器
        try {
          const element = document.querySelector(selector);
          if (element) {
            console.log('Found checkbox using direct selector');
            return element;
          }
        } catch (e) {
          console.log('Direct selector failed:', e.message);
        }
        
        // 最后回退：返回第一个 checkbox
        const allCheckboxes = document.querySelectorAll('input[type="checkbox"]');
        console.log('Falling back to first checkbox');
        return allCheckboxes[0] || null;
      }

      // 特殊处理：步骤/场景中的 Quill 编辑器
      if (
        selector.includes("data-step=") &&
        selector.includes("data-position=")
      ) {
        const stepMatch = selector.match(/data-step="([^"]+)"/);
        const positionMatch = selector.match(/data-position="([^"]+)"/);

        if (stepMatch && positionMatch) {
          const targetStep = stepMatch[1]; // "步骤1" 或 "场景1"
          const targetPosition = positionMatch[1]; // "step", "expect", "scenario"

          // 查找所有步骤/场景卡片
          const items = document.querySelectorAll(".item");
          for (let item of items) {
            const stepHeader = item.querySelector(".clearfix span");
            if (stepHeader && stepHeader.textContent.trim() === targetStep) {
              // 找到了对应的步骤/场景
              if (targetPosition === "scenario") {
                // 场景只有一个编辑器
                return item.querySelector(".ql-editor");
              } else {
                // 步骤有两个编辑器
                const cols = item.querySelectorAll(".el-col");
                if (targetPosition === "step" && cols[0]) {
                  return cols[0].querySelector(".ql-editor");
                } else if (targetPosition === "expect" && cols[1]) {
                  return cols[1].querySelector(".ql-editor");
                }
              }
            }
          }
        }

        // 如果没找到，返回 null
        return null;
      }

      // 特殊处理：Quill 编辑器的自定义选择器
      if (
        selector.includes("data-quill-label") ||
        selector.includes("data-quill-global-index")
      ) {
        // 提取 label 或 index 信息
        const labelMatch = selector.match(/data-quill-label="([^"]+)"/);
        const indexMatch = selector.match(
          /data-quill-(?:index|global-index)="(\d+)"/
        );

        const allEditors = document.querySelectorAll(".ql-editor");

        if (indexMatch) {
          const targetIndex = parseInt(indexMatch[1]);
          if (allEditors[targetIndex]) {
            return allEditors[targetIndex];
          }
        }

        // 如果有 label，尝试通过 label 查找
        if (labelMatch) {
          const labelText = labelMatch[1].replace(/-/g, " ").trim();
          for (let editor of allEditors) {
            const formItem = editor.closest(".el-form-item");
            if (formItem) {
              const label = formItem.querySelector("label");
              if (label) {
                const currentLabel = label.textContent
                  .trim()
                  .replace(/[^a-zA-Z0-9\u4e00-\u9fa5]/g, "-");
                if (currentLabel === labelMatch[1]) {
                  return editor;
                }
              }
            }
          }
        }

        // 如果都失败了，返回第一个编辑器
        return allEditors[0] || null;
      }

      // 1. 原始选择器查找
      let element = document.querySelector(selector);
      if (element) {
        // 如果有文本信息，验证文本是否匹配
        if (
          action &&
          action.text &&
          element.textContent.trim() === action.text
        ) {
          return element;
        }
        // 如果没有文本或文本为空，直接返回
        if (!action || !action.text) {
          return element;
        }
      }

      // 2. 移除nth-child/nth-of-type伪类重试
      const simplified1 = selector.replace(/:nth-(child|of-type)\(\d+\)/g, "");
      const elements = document.querySelectorAll(simplified1);

      // 如果有action.text，在所有匹配的元素中找到文本匹配的
      if (action && action.text && elements.length > 0) {
        for (let el of elements) {
          if (el.textContent.trim() === action.text) {
            return el;
          }
        }
      } else if (elements.length > 0) {
        return elements[0];
      }

      // 3. 只保留ID选择器部分
      if (selector.includes("#")) {
        const idMatch = selector.match(/#[^ >]+/);
        if (idMatch) {
          element = document.querySelector(idMatch[0]);
          if (element) return element;
        }
      }

      // 4. 如果有action.text，按文本内容精确查找
      if (action && action.text) {
        const tagMatch = selector.match(/([a-z]+)(?::nth-|$|\s)/);
        if (tagMatch) {
          const tagName = tagMatch[1];
          const allElements = document.getElementsByTagName(tagName);

          // 精确匹配文本内容
          for (let el of allElements) {
            if (el.textContent.trim() === action.text) {
              return el;
            }
          }

          // 模糊匹配（包含关系）
          for (let el of allElements) {
            if (el.textContent.includes(action.text)) {
              return el;
            }
          }
        }
      }

      // 5. 按标签名查找（最后的回退方案）
      const tagMatch = selector.match(/^[a-z]+/);
      if (tagMatch) {
        const tagName = tagMatch[0];
        const allElements = document.getElementsByTagName(tagName);
        for (let el of allElements) {
          // 如果有父ID，优先匹配父元素
          if (selector.includes("#")) {
            const parentId = selector.match(/#([^ >]+)/)[1];
            if (el.closest(`#${parentId}`)) {
              return el;
            }
          }
        }
      }

      return null;
    },

    // 等待元素出现（带超时）
    async waitForElement(selector, action = null, timeout = 10000) {
      const start = Date.now();
      
      // 修复选择器格式问题
      const fixedSelector = selector
        .replace(/@@/g, '__')  // 转换临时分隔符
        .replace(/el-checkbox\s+original/g, 'el-checkbox__original')
        .replace(/el-checkbox_original/g, 'el-checkbox__original')
        .replace(/input\.el-checkbox\s+original/g, 'input.el-checkbox__original')
        .replace(/input\.el-checkbox_original/g, 'input.el-checkbox__original')
        .replace(/el-input\s+inner/g, 'el-input__inner')
        .replace(/el-dialog\s+/g, 'el-dialog__');
      
      console.log('Waiting for element:', { 
        originalSelector: selector, 
        fixedSelector: fixedSelector, 
        action 
      });
      
      // 检查是否是 checkbox/radio（这些元素通常是隐藏的）
      const isCheckboxOrRadio = fixedSelector.includes('type="checkbox"') || 
                                 fixedSelector.includes('type="radio"') ||
                                 action?.type === 'checkbox' ||
                                 action?.type === 'radio';
      
      // 检查是否是 v-for 中的元素（可能需要等待数据加载）
      const isVForElement = fixedSelector.includes(':nth-of-type(');
      
      let lastFoundElement = null;
      let hiddenByVShow = false;
      
      while (Date.now() - start < timeout) {
        const element = this.findElement(fixedSelector, action);
        if (element) {
          lastFoundElement = element;
          
          // 对于 checkbox/radio，不检查可见性（因为它们通常是隐藏的）
          if (isCheckboxOrRadio) {
            console.log('Element found (checkbox/radio, skipping visibility check):', element);
            return element;
          }
          
          // 检查元素是否被 v-show 隐藏
          let current = element;
          let isHidden = false;
          
          while (current && current !== document.body) {
            const style = window.getComputedStyle(current);
            if (style.display === 'none') {
              isHidden = true;
              hiddenByVShow = true;
              console.log('Element found but hidden by v-show (display: none)');
              break;
            }
            if (style.visibility === 'hidden') {
              isHidden = true;
              console.log('Element found but hidden by visibility');
              break;
            }
            current = current.parentElement;
          }
          
          if (!isHidden) {
            // 元素可见，检查尺寸
            const rect = element.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
              console.log('Element found and visible:', element);
              return element;
            }
          }
        }
        await this.wait(200); // 增加等待间隔
      }
      
      // 超时后，如果找到了元素但被 v-show 隐藏，给出特殊提示
      if (lastFoundElement && hiddenByVShow) {
        console.warn('Element found but hidden by v-show, skipping this action');
        // 返回元素，让调用者决定如何处理
        // 或者抛出特殊错误
        throw new Error(`元素被 v-show 隐藏：${fixedSelector}（可能是数据条件不满足）`);
      }
      
      console.error('Element not found after timeout:', { 
        originalSelector: selector,
        fixedSelector: fixedSelector, 
        action 
      });
      const errorMsg =
        action && action.text
          ? `未找到元素：${fixedSelector}（文本："${action.text}"）`
          : `未找到元素：${fixedSelector}`;
      throw new Error(errorMsg);
    },

    wait(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    // 增强页面加载等待
    waitForPageLoad() {
      return new Promise((resolve) => {
        const checkLoad = () => {
          // 等待DOM就绪和网络空闲
          if (document.readyState === "complete") {
            setTimeout(resolve, 800); // 额外等待渲染完成
          } else {
            setTimeout(checkLoad, 100);
          }
        };
        checkLoad();
      });
    },
  },
};
</script>

<style scoped>
.global-player-control {
  z-index: 9999;
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 200px;
}
.script-preview {
  margin-top: 10px;
  padding: 10px;
  background: #f9f9f9;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  font-size: 12px;
}
.script-preview h5 {
  margin: 0 0 5px 0;
  font-size: 13px;
  color: #333;
}
.script-preview pre {
  margin: 0;
  white-space: pre-wrap;
  color: #666;
}
.file-input {
  display: block;
  margin-bottom: 10px;
  padding: 8px;
  width: 100%;
  box-sizing: border-box;
}
.play-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  background: #2f54eb;
  color: white;
  cursor: pointer;
  font-size: 14px;
}
.play-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}
.play-status {
  margin: 10px 0 0;
  font-size: 12px;
  color: #666;
  line-height: 1.5;
}
</style>